import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Truck, Package, Clock, MapPin, ChevronRight } from 'lucide-react';

// Breadcrumb schema for SEO
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.edenremedies.com"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Shipping Policy",
      "item": "https://www.edenremedies.com/shipping-policy"
    }
  ]
};

const ShippingPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#f5f1eb] py-12">
      {/* Breadcrumb Schema for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#2d4a47] transition-colors">Home</Link>
          <ChevronRight size={14} />
          <span className="text-gray-900">Shipping Policy</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-8">Shipping Policy</h1>
        
        <div className="bg-white p-8 rounded-lg shadow-sm space-y-8">
          {/* Shipping Rates */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Truck className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Shipping Rates</h2>
            </div>
            <div className="space-y-3 text-gray-600">
              <p><strong>Free Shipping:</strong> All orders over $30 qualify for free standard shipping within the United States.</p>
              <p><strong>Flat Rate Shipping:</strong> Orders under $30 ship for a flat rate of $8.95.</p>
            </div>
          </section>

          {/* Processing Time */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Clock className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Processing Time</h2>
            </div>
            <div className="space-y-3 text-gray-600">
              <p>Orders are typically processed within 1-2 business days. During peak seasons or promotional periods, processing may take up to 3-5 business days.</p>
              <p>You will receive a confirmation email with tracking information once your order has shipped.</p>
            </div>
          </section>

          {/* Delivery Times */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Package className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Estimated Delivery Times</h2>
            </div>
            <div className="space-y-3 text-gray-600">
              <p><strong>Standard Shipping:</strong> 5-7 business days</p>
              <p><strong>Expedited Shipping:</strong> 2-3 business days (additional charges may apply)</p>
              <p>Please note that delivery times are estimates and may vary depending on your location and carrier conditions.</p>
            </div>
          </section>

          {/* Shipping Locations */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Shipping Locations</h2>
            </div>
            <div className="space-y-3 text-gray-600">
              <p>We currently ship to all 50 US states. International shipping is not available at this time.</p>
              <p>We do not ship to PO Boxes or APO/FPO addresses at this time.</p>
            </div>
          </section>

          {/* Order Tracking */}
          <section>
            <h2 className="text-xl font-medium text-gray-900 mb-4">Order Tracking</h2>
            <div className="space-y-3 text-gray-600">
              <p>Once your order ships, you will receive an email with your tracking number. You can use this number to track your package on the carrier's website.</p>
              <p>If you have any questions about your order, please contact us at support@edenremedies.com.</p>
            </div>
          </section>

          {/* Eco-Friendly Packaging */}
          <section className="bg-[#f5f1eb] p-6 rounded-lg">
            <h2 className="text-xl font-medium text-gray-900 mb-4">Our Commitment to Sustainable Shipping</h2>
            <div className="space-y-3 text-gray-600">
              <p>At Eden Remedies, we believe in zero plastic packaging. All our products are shipped in:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>100% recyclable cardboard boxes</li>
                <li>Compostable packing materials</li>
                <li>Paper-based tape (no plastic tape)</li>
                <li>Recycled paper for any printed materials</li>
              </ul>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-6">
            <p className="text-gray-600">
              Have questions about shipping? Contact us at{' '}
              <a href="mailto:support@edenremedies.com" className="text-[#2d4a47] underline">
                support@edenremedies.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/products" className="text-[#2d4a47] underline hover:opacity-70">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicyPage;
